import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  viewChild,
  ElementRef,
} from '@angular/core';
import { ButtonDirective } from '../button.directive';
import { ConfirmService } from './confirm.service';

/**
 * A native <dialog> opened with showModal(), which renders in the browser's top layer.
 *
 * z-index cannot solve this: the outlet lives inside the app shell, and any ancestor that
 * creates a stacking context traps it there, while PrimeNG appends its own dialogs to
 * <body> and escapes. The top layer is above every stacking context by definition.
 * showModal() also brings the focus trap, inert background and Escape handling for free.
 */
@Component({
  selector: 'app-confirm-outlet',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonDirective],
  template: `
    <dialog
      #dialog
      class="max-w-md rounded-card bg-surface-card p-6 text-fg backdrop:bg-black/60"
      (close)="onNativeClose()"
      (cancel)="$event.preventDefault(); settle(false)"
    >
      @if (pending(); as request) {
        <p class="text-base">{{ request.message }}</p>
        <div class="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button appButton variant="secondary" (click)="settle(false)">
            {{ request.rejectLabel ?? 'Nee' }}
          </button>
          <button appButton variant="danger" (click)="settle(true)">
            {{ request.acceptLabel ?? 'Ja' }}
          </button>
        </div>
      }
    </dialog>
  `,
})
export class ConfirmOutletComponent {
  private readonly service = inject(ConfirmService);
  private readonly dialog =
    viewChild.required<ElementRef<HTMLDialogElement>>('dialog');

  protected readonly pending = this.service.pending;

  constructor() {
    effect(() => {
      const el = this.dialog().nativeElement;
      if (this.pending()) {
        if (!el.open) {
          el.showModal();
        }
      } else if (el.open) {
        el.close();
      }
    });
  }

  protected settle(accepted: boolean) {
    this.service.settle(accepted);
  }

  /** Covers the browser closing the dialog without one of our buttons. */
  protected onNativeClose() {
    if (this.pending()) {
      this.service.settle(false);
    }
  }
}
